import isEmpty from "lodash/isEmpty";
import values from "lodash/values";
import { DetailsPanelData } from "PFApp/booking/components/details_panel";
import { TemplateKey } from "PFApp/constants/templates";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useActivities } from "PFCore/hooks/queries/activities/use_activities";
import { ActivityWarningsIndex, useActivityWarnings } from "PFCore/hooks/queries/activity";
import { useProfiles } from "PFCore/hooks/queries/profiles/use_profiles";
import { KnownFiltersV2 } from "PFTypes/filters_v2";

export const useMisalignmentsData = (data: DetailsPanelData) => {
  const { activityIds, potentialMisalignments } = data.misalignmentDetails ?? {};

  const hasPotentialMisalignments = potentialMisalignments !== undefined;

  const { data: activitiesResponse, isFetching: isActivitiesFetching } = useActivities(
    {
      filters: {
        fields: {
          [KnownFiltersV2.Id]: activityIds,
          [KnownFiltersV2.TemplateKey]: [TemplateKey.Role, TemplateKey.AuditRole]
        }
      },
      perPage: PER_PAGE_UNPAGINATED
    },
    {
      enabled: !isEmpty(activityIds),
      retry: false
    }
  );

  const { warningsByActivity: fetchedWarningsByActivity, activityWarningsLoadingByRowKey } =
    useActivityWarnings(
      hasPotentialMisalignments ? [] : (activityIds ?? []).map((id) => ({ id: Number(id) }))
    );
  const isWarningsLoading = values(activityWarningsLoadingByRowKey).some((isLoading) => isLoading);

  const warningsByActivity: ActivityWarningsIndex = hasPotentialMisalignments
    ? activityIds && activityIds.length > 0
      ? { [Number(activityIds[0])]: potentialMisalignments }
      : {}
    : fetchedWarningsByActivity;

  const profileIds = values(warningsByActivity ?? {}).flatMap((activityWarnings) =>
    activityWarnings.flatMap(({ profileIds }) => profileIds)
  );
  const { data: profilesResponse, isLoading: isProfilesLoading } = useProfiles(
    {
      filters: {
        fields: { id: profileIds }
      },
      perPage: PER_PAGE_UNPAGINATED
    },
    { enabled: !isEmpty(profileIds) }
  );

  const activities = activitiesResponse?.entries.map(({ activity }) => activity) ?? [];
  const profiles = profilesResponse?.entries.map(({ profile }) => profile) ?? [];
  const isLoading = isProfilesLoading || isWarningsLoading || isActivitiesFetching;

  return { activities, profiles, warningsByActivity, isLoading };
};
